import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, TextField, ToggleButton, ToggleButtonGroup,
  Typography, useTheme,
} from '@mui/material';
import { DonutLargeOutlined, PieChartOutlineOutlined, ViewWeekOutlined } from '@mui/icons-material';
import {
  PieChart, Pie, Cell, Tooltip as RechartTooltip, ResponsiveContainer,
} from 'recharts';
import { ByteUnitSystem, formatDecimalMegabytes, formatStorageBytes } from './formatStorageBytes';
import { getInfralytiqsReportTableHeaderCellSx } from './infralytiqsReportTableTheme';

const CHART_COLORS = [
  '#10b981', '#f97316', '#ec4899', '#14b8a6', '#f59e0b', '#ef4444', '#84cc16', '#eab308',
];

/** Render modes available from the in-panel view switcher. */
type ViewMode = 'pie' | 'donut' | 'bar';

export interface SelectablePieChartProps {
  chartData: Record<string, unknown>[];
  nameKey: string;
  metricKey: string;
  visType: 'pie' | 'donut';
  /** Initial view from report config; falls back to `visType`. */
  defaultView?: ViewMode;
  valueFormat?: string;
  /** When `bytes`, show storage in GB on chips and tooltips if `byteDisplayUnit` is GB. */
  byteDisplayUnit?: 'auto' | 'GB';
  /** AEM REPORTS_API sizes use decimal (SI) bytes — default `decimal`. */
  byteUnitSystem?: ByteUnitSystem;
}

/**
 * Pie chart with clickable folder chips — toggle which slices are included.
 * Hides on-chart slice labels to reduce clutter; storage size is on each chip.
 */
export const SelectablePieChart: React.FC<SelectablePieChartProps> = ({
  chartData,
  nameKey,
  metricKey,
  visType,
  defaultView,
  valueFormat,
  byteDisplayUnit = 'GB',
  byteUnitSystem = 'decimal',
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const formatStorage = (value: number) => {
    if (valueFormat === 'decimal_mb') {
      return formatDecimalMegabytes(value);
    }
    return formatStorageBytes(value, {
      fixedUnit: valueFormat === 'bytes' && byteDisplayUnit === 'GB' ? 'GB' : undefined,
      system: byteUnitSystem,
    });
  };

  const allNames = useMemo(
    () => chartData.map((row) => String(row[nameKey] ?? '')).filter(Boolean),
    [chartData, nameKey],
  );

  const [selected, setSelected] = useState<Set<string>>(() => new Set(allNames));

  useEffect(() => {
    setSelected(new Set(allNames));
  }, [allNames.join('|')]);

  const colorByName = useMemo(() => {
    const map = new Map<string, string>();
    allNames.forEach((name, i) => {
      map.set(name, CHART_COLORS[i % CHART_COLORS.length]);
    });
    return map;
  }, [allNames]);

  const visibleData = useMemo(
    () => chartData.filter((row) => selected.has(String(row[nameKey] ?? ''))),
    [chartData, nameKey, selected],
  );

  const pieTotal = visibleData.reduce(
    (sum, row) => sum + (Number(row[metricKey]) || 0),
    0,
  );

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        if (next.size <= 1) return prev;
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const tooltipContentStyle: React.CSSProperties = {
    backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    boxShadow: theme.shadows[6],
    color: theme.palette.text.primary,
    padding: '6px 10px',
  };

  // The report config (defaultView) seeds the initial view, falling back to the
  // visualization type; users can still switch pie / donut / bar in-panel.
  const [viewMode, setViewMode] = useState<ViewMode>(defaultView ?? visType);
  useEffect(() => {
    setViewMode(defaultView ?? visType);
  }, [visType, defaultView]);

  const innerRadius = viewMode === 'donut' ? 60 : 0;

  // Biggest folder first; each row drives whether its slice shows in the pie.
  const sortedRows = useMemo(
    () =>
      chartData
        .map((row) => ({
          name: String(row[nameKey] ?? ''),
          value: Number(row[metricKey]) || 0,
        }))
        .filter((r) => r.name)
        .sort((a, b) => b.value - a.value),
    [chartData, nameKey, metricKey],
  );

  const [nameFilter, setNameFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');

  // Column sorting (default: biggest size first, matching the prior behavior).
  const [sortBy, setSortBy] = useState<'name' | 'value'>('value');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const handleSort = (col: 'name' | 'value') => {
    if (sortBy === col) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir(col === 'value' ? 'desc' : 'asc');
    }
  };

  const nf = nameFilter.trim().toLowerCase();
  const sf = sizeFilter.trim().toLowerCase();
  const filteredRows = sortedRows
    .filter((r) => {
      const sizeLabel = formatStorage(r.value).toLowerCase();
      return (
        (!nf || r.name.toLowerCase().includes(nf))
        && (!sf || sizeLabel.includes(sf))
      );
    })
    .slice()
    .sort((a, b) => {
      const cmp = sortBy === 'name'
        ? a.name.localeCompare(b.name)
        : a.value - b.value;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  return (
    <Box>
      <TableContainer
        sx={{
          // Fixed height (not maxHeight) so filtering never resizes the table:
          // rows scroll within this band and the pie chart below stays put.
          height: 300,
          mb: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Table size="small" stickyHeader aria-label="DAM folder sizes">
          <TableHead>
            <TableRow>
              <TableCell sx={getInfralytiqsReportTableHeaderCellSx(theme)} sortDirection={sortBy === 'name' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortDir : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Folder Name
                </TableSortLabel>
                <TextField
                  size="small"
                  variant="standard"
                  placeholder="Filter…"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ mt: 0.5, width: '100%' }}
                />
              </TableCell>
              <TableCell align="right" sx={getInfralytiqsReportTableHeaderCellSx(theme, { width: '34%' })} sortDirection={sortBy === 'value' ? sortDir : false}>
                <TableSortLabel
                  active={sortBy === 'value'}
                  direction={sortBy === 'value' ? sortDir : 'asc'}
                  onClick={() => handleSort('value')}
                >
                  Current Size
                </TableSortLabel>
                <TextField
                  size="small"
                  variant="standard"
                  placeholder="Filter…"
                  value={sizeFilter}
                  onChange={(e) => setSizeFilter(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ mt: 0.5, width: '100%' }}
                  inputProps={{ style: { textAlign: 'right' } }}
                />
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
                    No folders match the current filters.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((r) => {
                const isOn = selected.has(r.name);
                const color = colorByName.get(r.name) ?? CHART_COLORS[0];
                return (
                  <TableRow
                    key={r.name}
                    hover
                    onClick={() => toggle(r.name)}
                    sx={{
                      cursor: 'pointer',
                      opacity: isOn ? 1 : 0.5,
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Checkbox
                          size="small"
                          checked={isOn}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggle(r.name)}
                          sx={{ p: 0.5 }}
                        />
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            flexShrink: 0,
                            bgcolor: color,
                          }}
                        />
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                          {r.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={isOn ? 600 : 400}>
                        {formatStorage(r.value)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_e, next: ViewMode | null) => { if (next) setViewMode(next); }}
          aria-label="Chart view mode"
        >
          <ToggleButton value="pie" aria-label="Pie chart">
            <PieChartOutlineOutlined fontSize="small" />
          </ToggleButton>
          <ToggleButton value="donut" aria-label="Donut chart">
            <DonutLargeOutlined fontSize="small" />
          </ToggleButton>
          <ToggleButton value="bar" aria-label="Horizontal storage bar">
            <ViewWeekOutlined fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {visibleData.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          Select at least one folder above.
        </Typography>
      ) : viewMode === 'bar' ? (
        <Box sx={{ py: 1 }}>
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              height: 36,
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            {visibleData.map((row) => {
              const name = String(row[nameKey] ?? '');
              const value = Number(row[metricKey]) || 0;
              const widthPct = pieTotal > 0 ? (value / pieTotal) * 100 : 0;
              if (widthPct <= 0) return null;
              return (
                <Box
                  key={name}
                  title={`${name}: ${formatStorage(value)} (${widthPct.toFixed(1)}%)`}
                  sx={{
                    width: `${widthPct}%`,
                    minWidth: 2,
                    height: '100%',
                    bgcolor: colorByName.get(name) ?? CHART_COLORS[0],
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    transition: 'width 0.45s ease',
                  }}
                >
                  {widthPct >= 8 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#fff',
                        fontWeight: 600,
                        textShadow: '0 1px 2px rgba(0,0,0,0.35)',
                        whiteSpace: 'nowrap',
                        px: 0.5,
                      }}
                    >
                      {widthPct.toFixed(1)}%
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <PieChart>
            <Pie
              data={visibleData}
              dataKey={metricKey}
              nameKey={nameKey}
              innerRadius={innerRadius}
              outerRadius={100}
              paddingAngle={viewMode === 'donut' ? 2 : 0}
              label={false}
            >
              {visibleData.map((row) => {
                const name = String(row[nameKey] ?? '');
                return <Cell key={name} fill={colorByName.get(name) ?? CHART_COLORS[0]} />;
              })}
            </Pie>
            <RechartTooltip
              formatter={(v: unknown, name: string) => [
                formatStorage(Number(v) || 0),
                name,
              ]}
              contentStyle={tooltipContentStyle}
              itemStyle={{ color: theme.palette.text.primary }}
              labelStyle={{ color: theme.palette.text.primary }}
            />
          </PieChart>
        </ResponsiveContainer>
      )}
      {pieTotal > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
          Total ({visibleData.length} folder{visibleData.length === 1 ? '' : 's'}):{' '}
          {formatStorage(pieTotal)}
        </Typography>
      )}
    </Box>
  );
};
